import { GameState } from '../game/GameState';
import type { EspionageOperationData } from '../types/data';
import type { EspionageMissionState, SpyNetworkState } from '../types/game';
import { DataRegistry } from './DataRegistry';
import { DiplomacyManager } from './DiplomacyManager';
import { ModifierManager } from './ModifierManager';

export interface EspionageAction {
  id: string;
  name: string;
  cost: number;
  duration: number;
  successChance: number;
  failureConsequence: string;
  effect: string;
}

export const espionageActions: EspionageAction[] = [
  { id: 'gather_intelligence', name: 'Gather Intelligence', cost: 20, duration: 2, successChance: 86, failureConsequence: 'Network heat rises', effect: 'Reveal strategic readiness' },
  { id: 'map_defenses', name: 'Map Defenses', cost: 28, duration: 3, successChance: 76, failureConsequence: 'Scouts detained', effect: 'Military intelligence' },
  { id: 'infiltrate_port', name: 'Infiltrate Port', cost: 34, duration: 3, successChance: 72, failureConsequence: 'Harbor crackdown', effect: 'Naval intelligence' },
  { id: 'steal_knowledge', name: 'Steal Knowledge', cost: 52, duration: 5, successChance: 58, failureConsequence: 'Scholarly scandal', effect: 'Research diffusion' },
  { id: 'sow_distrust', name: 'Sow Distrust', cost: 38, duration: 4, successChance: 65, failureConsequence: 'Forgery exposed', effect: 'Damage foreign trust' },
  { id: 'fabricate_claim', name: 'Fabricate Claim', cost: 46, duration: 5, successChance: 63, failureConsequence: 'Diplomatic scandal', effect: 'Create a province claim' },
  { id: 'sabotage_market', name: 'Sabotage Market', cost: 44, duration: 4, successChance: 64, failureConsequence: 'Merchants testify', effect: 'Damage target markets' },
  { id: 'incite_separatists', name: 'Incite Separatists', cost: 62, duration: 6, successChance: 48, failureConsequence: 'Network suppressed', effect: 'Raise cultural unrest' },
  { id: 'support_pretender', name: 'Support Pretender', cost: 72, duration: 6, successChance: 43, failureConsequence: 'Court rallies', effect: 'Damage succession' },
  { id: 'disrupt_institution', name: 'Disrupt Institution', cost: 58, duration: 5, successChance: 52, failureConsequence: 'Scholars rally', effect: 'Slow institution spread' },
  { id: 'smuggle_weapons', name: 'Smuggle Weapons', cost: 56, duration: 5, successChance: 54, failureConsequence: 'Contraband seized', effect: 'Raise unrest and drain manpower' },
  { id: 'assassination_plot', name: 'Assassination Plot', cost: 105, duration: 8, successChance: 26, failureConsequence: 'Major diplomatic crisis', effect: 'Severe succession shock' },
];

export class EspionageManager {
  static readonly instance = new EspionageManager();

  ensureCampaignInitialized() {
    const campaign = GameState.instance.requireCampaign();
    campaign.spyNetworks ??= {};
    campaign.espionageHistory ??= [];
    campaign.espionageMissions ??= [];
    for (const faction of Object.values(campaign.factions)) faction.counterIntelligence ??= 20;
    for (const mission of campaign.espionageMissions) {
      const operation = this.operation(mission.actionId);
      mission.detectionChance ??= operation?.detectionRisk ?? 25;
      mission.networkCost ??= operation?.networkCost ?? 10;
      mission.startedTurn ??= Math.max(1, campaign.turn - Math.max(0, (operation?.duration ?? mission.remainingMonths) - mission.remainingMonths));
      mission.status ??= 'active';
      mission.discovered ??= false;
    }
  }

  network(sourceFactionId: string, targetFactionId: string, create = true) {
    this.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();
    const id = this.networkKey(sourceFactionId, targetFactionId);
    if (!campaign.spyNetworks[id] && create && campaign.factions[sourceFactionId] && campaign.factions[targetFactionId] && sourceFactionId !== targetFactionId) {
      campaign.spyNetworks[id] = { id, sourceFactionId, targetFactionId, strength: 0, infiltration: 0, counterIntelligence: 0, heat: 0, discovered: false, lastOperationTurn: 0 };
    }
    return campaign.spyNetworks[id];
  }

  establishNetwork(sourceFactionId: string, targetFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const source = campaign.factions[sourceFactionId];
    const network = this.network(sourceFactionId, targetFactionId);
    if (!source || !network || source.treasury < 30) {
      GameState.instance.log('Establishing a spy network requires 30 gold.', 'warning');
      return false;
    }
    if (network.strength >= 15) return true;
    source.treasury -= 30;
    network.strength = Math.max(15, network.strength + 15);
    network.infiltration = Math.max(8, network.infiltration + 8);
    network.heat += 3;
    GameState.instance.log(`A spy network was established in ${DataRegistry.instance.factions[targetFactionId]?.name ?? targetFactionId}.`, 'success');
    return true;
  }

  investNetwork(sourceFactionId: string, targetFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const source = campaign.factions[sourceFactionId];
    const network = this.network(sourceFactionId, targetFactionId);
    if (!source || !network || source.treasury < 35) {
      GameState.instance.log('Expanding a spy network requires 35 gold.', 'warning');
      return false;
    }
    source.treasury -= 35;
    network.strength = Math.min(100, network.strength + 14);
    network.infiltration = Math.min(100, network.infiltration + 9);
    network.heat = Math.min(100, network.heat + 4);
    network.discovered = false;
    GameState.instance.log(`The network in ${DataRegistry.instance.factions[targetFactionId]?.name ?? targetFactionId} expanded.`, 'success');
    return true;
  }

  strengthenCounterIntelligence(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    if (!faction || faction.treasury < 50 || faction.counterIntelligence >= 100) {
      GameState.instance.log('Counter-intelligence reform requires 50 gold.', 'warning');
      return false;
    }
    faction.treasury -= 50;
    faction.counterIntelligence = Math.min(100, faction.counterIntelligence + 10);
    for (const network of Object.values(campaign.spyNetworks).filter((entry) => entry.targetFactionId === factionId)) network.heat = Math.min(100, network.heat + 5);
    GameState.instance.log('Counter-intelligence offices were strengthened.', 'success');
    return true;
  }

  startMission(sourceFactionId: string, targetFactionId: string, actionId: string, targetProvinceId?: string) {
    this.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();
    const operation = this.operation(actionId);
    const source = campaign.factions[sourceFactionId];
    const target = campaign.factions[targetFactionId];
    const network = this.network(sourceFactionId, targetFactionId);
    if (!source || !target || !operation || !network) return false;
    if (operation.requiredTech && !source.researchedTechs.includes(operation.requiredTech)) {
      GameState.instance.log(`${operation.name} requires ${DataRegistry.instance.technologies[operation.requiredTech]?.name ?? operation.requiredTech}.`, 'warning');
      return false;
    }
    if (source.treasury < operation.cost) {
      GameState.instance.log(`${operation.name} requires ${operation.cost} gold.`, 'warning');
      return false;
    }
    if (network.strength < operation.networkRequired) {
      GameState.instance.log(`${operation.name} requires network strength ${operation.networkRequired}.`, 'warning');
      return false;
    }
    if (campaign.espionageMissions.some((mission) => mission.sourceFactionId === sourceFactionId && mission.targetFactionId === targetFactionId && mission.status === 'active')) {
      GameState.instance.log('Only one active operation may run through a network at a time.', 'warning');
      return false;
    }
    source.treasury -= operation.cost;
    network.strength = Math.max(0, network.strength - operation.networkCost);
    network.heat = Math.min(100, network.heat + operation.detectionRisk * 0.18);
    network.lastOperationTurn = campaign.turn;
    const modifiers = ModifierManager.instance.forFaction(sourceFactionId);
    const successChance = this.clamp(operation.baseSuccess + modifiers.espionageSuccessBonus + network.strength * 0.12 + network.infiltration * 0.24 - target.counterIntelligence * 0.22, 5, 95);
    const detectionChance = this.clamp(operation.detectionRisk + network.heat * 0.28 + target.counterIntelligence * 0.2 - modifiers.counterIntelligenceBonus * 0.25, 4, 95);
    campaign.espionageMissions.push({ id: `spy_${campaign.turn}_${campaign.espionageMissions.length}_${operation.id}`, actionId: operation.id, sourceFactionId, targetFactionId, targetProvinceId, remainingMonths: operation.duration, successChance, detectionChance, networkCost: operation.networkCost, startedTurn: campaign.turn, status: 'active', discovered: false });
    GameState.instance.log(`${operation.name} started against ${DataRegistry.instance.factions[targetFactionId]?.name}.`, sourceFactionId === campaign.playerFactionId ? 'success' : 'info');
    return true;
  }

  cancelMission(missionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const mission = campaign.espionageMissions.find((entry) => entry.id === missionId);
    if (!mission || mission.status !== 'active') return false;
    mission.status = 'cancelled';
    this.addHistory(mission, 'cancelled', 'The operation was cancelled before completion.');
    campaign.espionageMissions = campaign.espionageMissions.filter((entry) => entry.id !== missionId);
    return true;
  }

  processMonth() {
    this.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();
    this.processNetworks();
    const completed: EspionageMissionState[] = [];
    for (const mission of campaign.espionageMissions) {
      if (mission.status !== 'active') continue;
      mission.remainingMonths--;
      if (mission.remainingMonths <= 0) completed.push(mission);
    }
    for (const mission of completed) this.resolveMission(mission);
    campaign.espionageMissions = campaign.espionageMissions.filter((mission) => mission.status === 'active');
    if (campaign.turn % 3 === 0) this.processAi();
  }

  operation(actionId: string) {
    return DataRegistry.instance.espionageOperations[actionId] ?? this.legacyOperation(actionId);
  }

  private processNetworks() {
    const campaign = GameState.instance.requireCampaign();
    for (const network of Object.values(campaign.spyNetworks)) {
      const source = campaign.factions[network.sourceFactionId];
      const target = campaign.factions[network.targetFactionId];
      if (!source || !target) continue;
      const modifiers = ModifierManager.instance.forFaction(source.id);
      const treatyBonus = source.treaties.trade.includes(target.id) || source.treaties.militaryAccess.includes(target.id) ? 0.7 : 0;
      const activePenalty = campaign.espionageMissions.some((mission) => mission.sourceFactionId === source.id && mission.targetFactionId === target.id && mission.status === 'active') ? 0.5 : 0;
      if (network.strength > 0) {
        network.strength = Math.min(100, network.strength + 0.8 + modifiers.spyNetworkGrowthBonus * 10 + treatyBonus - activePenalty);
        network.infiltration = Math.min(100, network.infiltration + network.strength * 0.008 + treatyBonus * 0.2);
        network.heat = Math.max(0, network.heat - 1.2 - modifiers.counterIntelligenceBonus * 0.04);
      }
      const discoveryChance = this.clamp(network.heat * 0.18 + target.counterIntelligence * 0.1 - network.infiltration * 0.06, 0, 35);
      if (!network.discovered && this.roll(`${campaign.turn}:${network.id}:discover`) < discoveryChance) {
        network.discovered = true;
        network.strength = Math.max(0, network.strength - 18);
        network.infiltration = Math.max(0, network.infiltration - 12);
        target.counterIntelligence = Math.min(100, target.counterIntelligence + 2);
        GameState.instance.log(`${DataRegistry.instance.factions[target.id]?.name ?? target.id} uncovered a foreign spy network.`, target.id === campaign.playerFactionId ? 'danger' : 'warning');
      }
      if (network.discovered && network.heat < 15) network.discovered = false;
    }
    for (const faction of Object.values(campaign.factions)) {
      const modifiers = ModifierManager.instance.forFaction(faction.id);
      faction.counterIntelligence = this.clamp(faction.counterIntelligence + 0.05 + modifiers.counterIntelligenceBonus * 0.01, 0, 100);
    }
  }

  private resolveMission(mission: EspionageMissionState) {
    const campaign = GameState.instance.requireCampaign();
    const operation = this.operation(mission.actionId);
    const target = campaign.factions[mission.targetFactionId];
    const source = campaign.factions[mission.sourceFactionId];
    const network = this.network(mission.sourceFactionId, mission.targetFactionId, false);
    if (!operation || !target || !source) return;
    const success = this.roll(`${campaign.turn}:${mission.id}:success`) < mission.successChance;
    const exposed = this.roll(`${campaign.turn}:${mission.id}:detect`) < mission.detectionChance;
    mission.discovered = exposed;
    if (success) {
      mission.status = exposed ? 'exposed' : 'succeeded';
      this.applyMissionSuccess(operation, mission);
      source.prestige = Math.min(100, source.prestige + (exposed ? 1 : 2));
      this.addHistory(mission, 'success', `${operation.name} succeeded${exposed ? ' but the network was exposed' : ''}.`);
      GameState.instance.log(`${operation.name} succeeded against ${DataRegistry.instance.factions[target.id]?.name}.`, mission.sourceFactionId === campaign.playerFactionId ? 'success' : 'info');
    } else {
      mission.status = exposed ? 'exposed' : 'failed';
      source.prestige = Math.max(0, source.prestige - (exposed ? 5 : 2));
      target.prestige = Math.min(100, target.prestige + 1);
      target.counterIntelligence = Math.min(100, target.counterIntelligence + (exposed ? 4 : 1));
      this.addHistory(mission, exposed ? 'exposed' : 'failure', `${operation.name} failed: ${operation.failureEffect}.`);
      GameState.instance.log(`${operation.name} failed: ${operation.failureEffect}.`, mission.sourceFactionId === campaign.playerFactionId ? 'danger' : 'warning');
    }
    if (exposed) {
      if (network) {
        network.discovered = true;
        network.heat = Math.min(100, network.heat + 30);
        network.strength = Math.max(0, network.strength - 24);
      }
      this.applyExposureDiplomacy(mission.sourceFactionId, mission.targetFactionId);
    }
  }

  private applyMissionSuccess(operation: EspionageOperationData, mission: EspionageMissionState) {
    const campaign = GameState.instance.requireCampaign();
    const target = campaign.factions[mission.targetFactionId];
    const source = campaign.factions[mission.sourceFactionId];
    const network = this.network(source.id, target.id, false);
    const targetProvinces = Object.values(campaign.provinces).filter((province) => province.owner === target.id);
    const targetProvince = mission.targetProvinceId ? campaign.provinces[mission.targetProvinceId] : targetProvinces.sort((a, b) => b.prosperity - a.prosperity)[0];
    switch (operation.id) {
      case 'gather_intelligence':
        if (network) network.infiltration = Math.min(100, network.infiltration + 14);
        campaign.scenarioFlags[`intel:${source.id}:${target.id}`] = true;
        break;
      case 'map_defenses':
        source.militaryTradition = Math.min(100, source.militaryTradition + 3);
        campaign.scenarioFlags[`defense-map:${source.id}:${target.id}`] = true;
        break;
      case 'infiltrate_port': {
        const port = targetProvinces.filter((province) => DataRegistry.instance.provinces[province.id]?.port).sort((a, b) => b.prosperity - a.prosperity)[0];
        if (port) port.prosperity = Math.max(0, port.prosperity - 4);
        source.navalTradition = Math.min(100, source.navalTradition + 3);
        break;
      }
      case 'steal_knowledge': {
        const technologyId = target.researchedTechs.find((technology) => !source.researchedTechs.includes(technology));
        if (technologyId) {
          source.research.diffusionProgress[technologyId] = Math.min(100, (source.research.diffusionProgress[technologyId] ?? 0) + 45);
          if (source.research.activeTechnologyId === technologyId) source.research.activeProgress += 30;
        } else source.research.activeProgress += 25;
        break;
      }
      case 'sow_distrust': {
        target.diplomaticReputation = Math.max(-20, target.diplomaticReputation - 4);
        const partnerId = [...target.treaties.alliances, ...target.treaties.trade][0];
        if (partnerId) {
          const relation = campaign.diplomaticRelations[`${target.id}:${partnerId}`];
          if (relation) relation.trust = Math.max(-100, relation.trust - 16);
        }
        break;
      }
      case 'fabricate_claim':
        if (targetProvince && !source.claims.includes(targetProvince.id)) source.claims.push(targetProvince.id);
        break;
      case 'sabotage_market':
        for (const province of targetProvinces.filter((entry) => DataRegistry.instance.provinces[entry.id]?.port).slice(0, 2)) {
          province.prosperity = Math.max(0, province.prosperity - 8);
          for (const goodId of Object.keys(province.marketPrices)) province.marketPrices[goodId] = Math.round(province.marketPrices[goodId] * 1.08 * 100) / 100;
        }
        break;
      case 'incite_separatists':
        for (const province of targetProvinces.sort((a, b) => b.culturalTension - a.culturalTension).slice(0, 2)) {
          province.unrest = Math.min(100, province.unrest + 12);
          province.culturalTension = Math.min(100, province.culturalTension + 15);
        }
        break;
      case 'support_pretender':
        target.legitimacy = Math.max(0, target.legitimacy - 10);
        if (target.dynasty) target.dynasty.successionClarity = Math.max(0, target.dynasty.successionClarity - 14);
        break;
      case 'disrupt_institution': {
        const institutionId = Object.entries(target.research.institutionProgress).filter(([id]) => !target.research.adoptedInstitutions.includes(id)).sort((a, b) => b[1] - a[1])[0]?.[0];
        if (institutionId) target.research.institutionProgress[institutionId] = Math.max(0, target.research.institutionProgress[institutionId] - 25);
        target.research.activeProgress = Math.max(0, target.research.activeProgress - 18);
        break;
      }
      case 'smuggle_weapons':
        for (const province of targetProvinces.slice(0, 2)) province.unrest = Math.min(100, province.unrest + 9);
        target.manpower = Math.max(0, target.manpower - Math.round(target.maxManpower * 0.05));
        break;
      case 'assassination_plot': {
        target.legitimacy = Math.max(0, target.legitimacy - 14);
        if (target.dynasty) target.dynasty.successionClarity = Math.max(0, target.dynasty.successionClarity - 18);
        const ruler = target.dynasty?.rulerId ? campaign.characters[target.dynasty.rulerId] : undefined;
        if (ruler) ruler.health = Math.max(5, ruler.health - 25);
        break;
      }
    }
  }

  private applyExposureDiplomacy(sourceFactionId: string, targetFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const targetRelation = DiplomacyManager.instance.relation(targetFactionId, sourceFactionId);
    if (targetRelation) {
      targetRelation.trust = Math.max(-100, targetRelation.trust - 22);
      targetRelation.threat = Math.min(100, targetRelation.threat + 14);
      targetRelation.modifiers.exposed_spy_operation = -18;
    }
    const source = campaign.factions[sourceFactionId];
    if (source) source.diplomaticReputation = Math.max(-25, source.diplomaticReputation - 5);
  }

  private processAi() {
    const campaign = GameState.instance.requireCampaign();
    for (const source of Object.values(campaign.factions)) {
      if (source.id === campaign.playerFactionId) continue;
      const candidates = Object.values(campaign.factions).filter((target) => target.id !== source.id).sort((a, b) => DiplomacyManager.instance.opinion(source.id, a.id) - DiplomacyManager.instance.opinion(source.id, b.id));
      const target = candidates[0];
      if (!target) continue;
      const network = this.network(source.id, target.id);
      if (!network) continue;
      if (network.strength < 20 && source.treasury > 100) this.establishNetwork(source.id, target.id);
      else if (network.strength < 55 && source.treasury > 150) this.investNetwork(source.id, target.id);
      else if (!campaign.espionageMissions.some((mission) => mission.sourceFactionId === source.id && mission.status === 'active')) {
        const operations = DataRegistry.instance.espionageOperationList().filter((operation) => network.strength >= operation.networkRequired && (!operation.requiredTech || source.researchedTechs.includes(operation.requiredTech))).sort((a, b) => a.detectionRisk - b.detectionRisk || a.cost - b.cost);
        const operation = operations[(campaign.turn + source.id.length) % Math.max(1, operations.length)];
        if (operation && source.treasury > operation.cost + 50) this.startMission(source.id, target.id, operation.id);
      }
      if (source.counterIntelligence < 45 && source.treasury > 180 && campaign.turn % 6 === 0) this.strengthenCounterIntelligence(source.id);
    }
  }

  private addHistory(mission: EspionageMissionState, outcome: 'success' | 'failure' | 'exposed' | 'cancelled', description: string) {
    const campaign = GameState.instance.requireCampaign();
    campaign.espionageHistory.unshift({ id: `spy_history_${mission.id}_${campaign.turn}`, turn: campaign.turn, sourceFactionId: mission.sourceFactionId, targetFactionId: mission.targetFactionId, actionId: mission.actionId, outcome, description });
    campaign.espionageHistory = campaign.espionageHistory.slice(0, 100);
  }

  private legacyOperation(actionId: string): EspionageOperationData | undefined {
    const action = espionageActions.find((entry) => entry.id === actionId);
    if (!action) return undefined;
    return { id: action.id, name: action.name, category: 'subversion', cost: action.cost, duration: action.duration, networkRequired: 15, networkCost: 8, baseSuccess: action.successChance, detectionRisk: 25, description: action.effect, successEffect: action.effect, failureEffect: action.failureConsequence };
  }

  private networkKey(sourceFactionId: string, targetFactionId: string) { return `${sourceFactionId}:${targetFactionId}`; }
  private clamp(value: number, minimum: number, maximum: number) { return Math.max(minimum, Math.min(maximum, value)); }
  private roll(seed: string) { return [...seed].reduce((sum, character, index) => ((sum * 31) + character.charCodeAt(0) + index) % 1000003, 17) % 100; }
}